/* eslint-disable filenames/match-exported */
/* eslint-disable filenames/match-regex */

import * as d3 from 'd3';

import ChartTooltip from '@/chart/chartTooltip';

const marginTop = 6;
const marginRight = 12;
const marginBottom = 16;
const marginLeft = 21 + marginRight;
const marginLeftDualAxis = 32;
// const marginRightDualAxis = 32;

export default class LineChart {
  svg = null;
  width = null;
  height = null;
  xScaleType = null;
  yScaleType = null;
  y2ScaleType = null;

  xScale = null;
  yScale = null;
  y2Scale = null;

  xAxisTicks = 'auto';
  // Functions or null
  xAxisFormat = null;

  // Functions or null
  yAxisFormat = null;
  y2AxisFormat = null;

  useCommonYScale = false;

  isDarkTheme = false;

  #dataCollect = [];
  #dataCollectSecond = [];

  constructor(
    element,
    width,
    height,
    options,
  ) {
    const defaults = {
      xAxisTicks: 'auto',
      xAxisFormat: null,
      xScaleType: 'scaleUtc',
      yScaleType: 'scaleLinear',
      y2ScaleType: 'scaleLinear',
      yAxisFormat: null,
      y2AxisFormat: null,
      useCommonYScale: false,
      isDarkTheme: false,
    };

    options = Object.assign({}, defaults, options);

    this.width = width;
    this.height = height;
    this.xAxisTicks = options.xAxisTicks;
    this.xAxisFormat = options.xAxisFormat;
    this.xScaleType = options.xScaleType;
    this.yScaleType = options.yScaleType;
    this.y2ScaleType = options.y2ScaleType;
    this.yAxisFormat = options.yAxisFormat;
    this.y2AxisFormat = options.y2AxisFormat;
    this.useCommonYScale = options.useCommonYScale;
    this.isDarkTheme = options.isDarkTheme;

    this.svg = d3.select(element)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [ 0, 0, width, height ])
      .attr('style', 'max-width: 100%; height: auto; height: intrinsic;');
  }

  addData(details) {
    details.chart = this;
    this.#dataCollect.push(details);
  }

  addDataSecond(details) {
    details.chart = this;
    this.#dataCollectSecond.push(details);
  }

  get data() {
    return this.#dataCollect;
  }

  get dataSecond() {
    return this.#dataCollectSecond;
  }

  render() {
    this._renderAxes();

    this._renderLinesFor(this.#dataCollect, this.yScale);
    if (this.#dataCollectSecond.length > 0) {
      this._renderLinesFor(this.#dataCollectSecond, this.y2Scale);
    }

    this.svg
      .on('pointerenter pointermove', event => this._onPointerMoved(event))
      .on('pointerleave', event => this._onPointerLeft(event));
  }

  _renderLinesFor(dataCollection, yScaleFn) {
    dataCollection.forEach((details, key) => {
      const line = d3.line()
        .x(d => this.xScale(d[details.xKey]))
        .y(d => yScaleFn(d[details.yKey]))
        //.curve(d3.curveCatmullRom.alpha(0));
        .curve(d3.curveMonotoneX);

      const shadowFilterId = 'line-shadow-' + key;

      const defs = this.svg.append('defs');
      const filter = defs.append('filter')
        .attr('id', shadowFilterId)
        .attr('x', '-50%')
        .attr('y', '-50%')
        .attr('width', '200%')
        .attr('height', '300%');

      filter.append('feComponentTransfer')
        .attr('in', 'SourceGraphic')
        .attr('result', 'alphaOut')
        .append('feFuncA').attr('type', 'linear').attr('slope', 0.25);

      filter.append('feOffset')
        .attr('in', 'alphaOut')
        .attr('result', 'offOut')
        .attr('dx', '0')
        .attr('dy', '5');

      filter.append('feGaussianBlur')
        .attr('in', 'offOut')
        .attr('result', 'blurOut')
        .attr('stdDeviation', '15');

      filter.append('feBlend')
        .attr('in', 'SourceGraphic')
        .attr('in2', 'blurOut')
        .attr('mode', 'normal');

      this.svg.append('path')
        .attr('fill', 'none')
        .attr('stroke', details.color)
        .attr('stroke-width', '3px')
        .attr('stroke-linecap', 'round')
        .attr('d', line(details.data))
        .attr('filter', 'url(#' + shadowFilterId + ')');
    });

    dataCollection.forEach(details => {
      if (details.tooltip instanceof ChartTooltip) {
        details.tooltip.color = details.color;
        details.tooltip.bisect = d3.bisector(d => d[details.xKey]).center;
      }
    });
  }

  _onPointerMoved(event) {
    const updateDetailsOnDataCollection = (collection, yScale) => collection.forEach((details, index) => {
      // 3 means 3px stroke-width
      details.updateDynamicDetails(event, this.xScale, yScale, index, collection.length, 3);
    });

    updateDetailsOnDataCollection(this.#dataCollect, this.yScale);
    if (this.#dataCollectSecond.length > 0) {
      updateDetailsOnDataCollection(this.#dataCollectSecond, this.y2Scale);
    }
  }

  _onPointerLeft() {
    const hideDetailsOnDataCollection = collection => collection.forEach(details => {
      details.hideOnHoverDetails();
    });

    hideDetailsOnDataCollection(this.#dataCollect);
    if (this.#dataCollectSecond.length > 0) {
      hideDetailsOnDataCollection(this.#dataCollectSecond);
    }
  }

  _renderAxes() {
    const xValues = [];
    const yValues = [];
    const y2Values = [];

    const mergedData = [];
    const mergedDataSecond = [];

    this.#dataCollect.forEach(details => {
      const transformedData = details.data.map(i => ({ x: i[details.xKey], y: i[details.yKey] }));
      mergedData.push( ...transformedData );

      xValues.push( ...details.data.map(i => i[details.xKey]) );
      yValues.push( ...details.data.map(i => i[details.yKey]) );
    });

    let xScaleEndPoint = this.width - marginRight;

    if (this.#dataCollectSecond.length > 0) {
      xScaleEndPoint = this.width - marginLeft;

      this.#dataCollectSecond.forEach(details => {
        const transformedData = details.data.map(i => ({ x: i[details.xKey], y: i[details.yKey] }));
        mergedDataSecond.push( ...transformedData );

        xValues.push( ...details.data.map(i => i[details.xKey]) );
        y2Values.push( ...details.data.map(i => i[details.yKey]) );
      });
    }

    let yScaleMax = d3.max(mergedData, d => d.y);
    let y2ScaleMax = d3.max(mergedDataSecond, d => d.y);
    if (this.#dataCollectSecond.length > 0) {
      if (this.useCommonYScale) {
        if (yScaleMax > y2ScaleMax) {
          y2ScaleMax = yScaleMax;
        }
        else {
          yScaleMax = y2ScaleMax;
        }
      }
    }

    const yScaleMaxScaleIncrement = Math.ceil(yScaleMax * 0.1);
    const y2ScaleMaxScaleIncrement = Math.ceil(y2ScaleMax * 0.1);

    if (this.xScaleType == 'scaleUtc' || this.xScaleType == 'scaleDate') {
      this.xScale = d3[this.xScaleType](d3.extent(mergedData, d => d.x), [ marginLeft, xScaleEndPoint ]);
    }
    else if (this.xScaleType == 'scaleBand' || this.xScaleType == 'scalePoint') {
      const xKey = this.#dataCollect[0].xKey;
      this.xScale = d3[this.xScaleType]()
        .domain(this.#dataCollect[0].data.map(d => d[xKey]))
        .range([ marginLeft, xScaleEndPoint ])
        .padding(0)
      ;
    }
    else {
      throw new Error('Not implemented');
    }

    this.yScale = d3[this.yScaleType]([ 0, yScaleMax + yScaleMaxScaleIncrement ], [ this.height - marginBottom, marginTop ]);
    this.y2Scale = d3[this.y2ScaleType]([ 0, y2ScaleMax + y2ScaleMaxScaleIncrement ], [ this.height - marginBottom, marginTop ]);

    this._renderXAxis();
    this._renderYAxisWithGridLines();
    this._renderY2AxisWithGridLines();
  }

  _renderXAxis() {
    let xAxisBottom = d3.axisBottom(this.xScale).tickSizeOuter(0);
    if (typeof this.xAxisTicks === 'number') {
      xAxisBottom = xAxisBottom.ticks(this.xAxisTicks);
    }
    else if (Array.isArray(this.xAxisTicks)) {
      xAxisBottom = xAxisBottom.tickValues(this.xAxisTicks);
    }
    else {
      xAxisBottom = xAxisBottom.ticks(this.width / 80);
    }

    if (this.xAxisFormat !== null) {
      xAxisBottom = xAxisBottom.tickFormat(this.xAxisFormat);
    }

    this.svg.append('g')
      .attr('transform', 'translate(0, ' + (this.height - marginBottom) + ')')
      .call(xAxisBottom)
      .call(g => {
        g.select('.domain').remove();
        g.selectAll('.tick line').remove();
        g.selectAll('.tick text')
          .attr('fill', this.isDarkTheme ? '#A3AED0' : '#8B93AE')
          .attr('dy', '0.6em')
          .style('font-size', '9px')
          .style('font-weight', '500')
          .style('cursor', 'default');
      });
  }

  _renderYAxisWithGridLines() {
    let calculatedMarginLeft = marginLeft;

    // In case of DUAL AXIS is in use..
    if (this.#dataCollectSecond.length > 0) {
      calculatedMarginLeft = marginLeftDualAxis;
    }

    const gridLineEndX = this.#dataCollectSecond.length > 0
      ? this.width - calculatedMarginLeft
      : this.width - marginRight;

    let yAxisCallee = d3.axisLeft(this.yScale).ticks(this.height / 40);
    if (typeof this.yAxisFormat == 'function') {
      yAxisCallee = yAxisCallee.tickFormat(this.yAxisFormat);
    }

    this.svg.append('g')
      .call(yAxisCallee)
      .call(g => {
        g.select('.domain').remove();
        g.selectAll('.tick line')
          .attr('x1', calculatedMarginLeft)
          .attr('x2', gridLineEndX)
          .attr('stroke', this.isDarkTheme ? '#656565' : '#D1D7E4')
          .attr('stroke-dasharray', '6');
        g.selectAll('.tick text')
          .attr('fill', '#A3AED0')
          .style('font-size', '9px')
          .style('font-weight', '500')
          .style('cursor', 'default')
          .attr('x', 0);
      })
      .attr('text-anchor', 'start');
  }

  _renderY2AxisWithGridLines() {
    let yAxisCallee = d3.axisRight(this.y2Scale).ticks(this.height / 40);
    if (typeof this.y2AxisFormat == 'function') {
      yAxisCallee = yAxisCallee.tickFormat(this.y2AxisFormat);
    }

    this.svg.append('g')
      .attr('transform', `translate(${marginLeft}, 0)`)
      .call(yAxisCallee)
      .call(g => {
        g.select('.domain').remove();
        g.selectAll('.tick line').remove();
        g.selectAll('.tick text')
          .attr('fill', '#A3AED0')
          .attr('text-anchor', 'end')
          .style('font-size', '9px')
          .style('font-weight', '500')
          .style('cursor', 'default')
          .attr('x', `${this.width - marginLeft}`);
      });
  }
}
